package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.enginetabular.TabularBulkEditRequest
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

internal fun resolveScriptDefinitions(items: List<All>, scripts: List<All>): List<All> {
    val text = scripts.filter { it.scriptTyp == "Text" || it.scriptTyp == "Frage" }.associateBy { it.id }
    val antworten = scripts.filter { it.scriptTyp == "Antwort" }.associateBy { it.id }
    val einwaende = scripts.filter { it.scriptTyp == "Einwand" }.associateBy { it.id }
    val reaktionen = scripts.filter { it.scriptTyp == "Reaktion" }.associateBy { it.id }
    return items.map { item ->
        item.copy(
            akquiseDefinitionText = text[item.akquiseDefinitionScriptId]?.scriptInhalt ?: item.akquiseDefinitionText,
            akquiseDefinitionAntworten = item.akquiseDefinitionAntwortIds.takeIf { it.isNotEmpty() }
                ?.mapIndexed { index, id ->
                    val source = if (item.akquiseDefinitionAntwortTypen.getOrNull(index) == "Einwand") einwaende else antworten
                    source[id]?.scriptInhalt ?: item.flowAntworten().getOrNull(index).orEmpty()
                }
                ?: item.flowAntworten(),
            akquiseDefinitionReaktionen = item.akquiseDefinitionReaktionIds.takeIf { it.isNotEmpty() }
                ?.mapIndexed { index, id -> reaktionen[id]?.scriptInhalt ?: item.flowReaktionen().getOrNull(index).orEmpty() }
                ?: item.flowReaktionen()
        )
    }
}

private fun scriptRowKey(item: All) = "${item.scriptTyp}:${item.id}"

@Composable
internal fun ScriptIndexDialog(scripts: List<All>, onChange: (List<All>) -> Unit, onClose: () -> Unit,
    rollenKey: String = "AkquiseEinstellungen", bereich: String = "CALL") {
    val scope = rememberCoroutineScope()
    var error by remember { mutableStateOf("") }
    var version by remember { mutableStateOf(0) }
    var selectedRows by remember { mutableStateOf<Set<String>>(emptySet()) }
    var draftRows by remember { mutableStateOf<List<Pair<String, List<String>>>>(emptyList()) }
    var bulkEditRequest by remember { mutableStateOf<TabularBulkEditRequest?>(null) }
    var bulkEditNonce by remember { mutableStateOf(0) }
    val types = listOf("Text", "Frage", "Antwort", "Einwand", "Reaktion")

    suspend fun reload() {
        val loaded = VertriebsindexApi.loadScriptIndex(bereich)
        onChange(loaded)
        selectedRows = emptySet()
        draftRows = emptyList()
        bulkEditRequest = null
        version++
    }

    PopupEngineCard(windowKey = "vertrieb-script-index-$bereich", zIndex = POPUP_ENGINE_MODAL_Z_INDEX + 40,
        widthPx = 1080, heightPx = 760, minWidthPx = 650, minHeightPx = 400,
        centered = true, viewportAnchored = true, resizable = true, onClose = onClose) {
        Div({ style { width(100.percent); height(100.percent); minHeight(0.px); padding(12.px); boxSizing("border-box"); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column) } }) {
            Div({ attr("role", "heading"); attr("aria-level", "2"); style { textAlign("center"); fontSize(20.px); fontWeight("700"); padding(12.px); marginBottom(12.px); flexShrink(0) } }) { Text(if (bereich == "TERMIN") "Termin Script" else "Call Script") }
            if (error.isNotBlank()) Div({ attr("role", "alert"); style { padding(8.px); color(Color("#ffb4b4")) } }) { Text(error) }
            key(version) {
                TabularScreen(
                    screenKey = if (bereich == "TERMIN") "VertriebTerminScriptIndex" else "VertriebScriptIndex",
                    entityScope = if (bereich == "TERMIN") "vertrieb-termin-scripte" else "vertrieb-scripte",
                    rollenKey = rollenKey,
                    title = null,
                    headers = listOf("scriptTyp", "scriptInhalt"),
                    headerDisplayLabels = listOf("Typ", "Inhalt"),
                    models = scripts.map { it.copy(id = scriptRowKey(it)) },
                    data = scripts.map { scriptRowKey(it) to listOf(it.scriptTyp, it.scriptInhalt) },
                    editable = true,
                    commitChangedRowsOnly = true,
                    includeMetadata = false,
                    smartMode = true,
                    multilineColumns = setOf("scriptInhalt"),
                    nonEditableCols = setOf("scriptTyp"),
                    onSelectedRowsChange = { selectedRows = it },
                    onDraftChange = { draft ->
                        draftRows = draft
                    },
                    bulkEditRequest = bulkEditRequest,
                    extraNavButtons = listOf(TabularToolbarButton(
                        label = "Typ", icon = "images/filter.svg",
                        enabled = selectedRows.isNotEmpty(),
                        dropdownOptions = types.map { it to it },
                        selectedDropdownValue = (draftRows.ifEmpty { scripts.map { scriptRowKey(it) to listOf(it.scriptTyp, it.scriptInhalt) } })
                            .filter { it.first in selectedRows }.map { it.second.firstOrNull().orEmpty() }.distinct().singleOrNull(),
                        dropdownPlaceholder = "Typ zuweisen",
                        onDropdownChange = { selectedTyp ->
                            if (selectedTyp != null && selectedRows.isNotEmpty()) {
                                bulkEditNonce++
                                bulkEditRequest = TabularBulkEditRequest(bulkEditNonce, selectedRows, "scriptTyp", selectedTyp)
                            }
                        }
                    )),
                    createButtonLabel = "Script anlegen",
                    createRow = { listOf("Text", "") },
                    onRefresh = { scope.launch {
                        try { reload(); error = "" }
                        catch (e: CancellationException) { throw e }
                        catch (e: Exception) { error = e.message ?: "Index konnte nicht geladen werden." }
                    } },
                    onCommit = {},
                    onCommitConfirmed = { rows, complete ->
                        scope.launch {
                            try {
                                if (rows.isEmpty()) {
                                    error = ""
                                    complete(true)
                                } else if (rows.any { (_, columns) -> columns.getOrNull(0) !in types || columns.getOrNull(1).isNullOrBlank() }) {
                                    error = "Jede Zeile benötigt einen gültigen Typ und Inhalt."
                                    complete(false)
                                } else {
                                    val changes = rows.map { (rowId, columns) ->
                                        val known = scripts.firstOrNull { scriptRowKey(it) == rowId }
                                        VertriebsindexApi.ScriptAenderung(known?.id.orEmpty(), known?.scriptTyp.orEmpty(),
                                            columns[0], columns[1])
                                    }
                                    val loaded = VertriebsindexApi.saveScripts(changes, bereich)
                                    error = ""
                                    complete(true)
                                    onChange(loaded)
                                    selectedRows = emptySet()
                                    draftRows = emptyList()
                                    bulkEditRequest = null
                                    version++
                                }
                            } catch (e: CancellationException) { complete(false); throw e }
                            catch (e: Exception) { error = e.message ?: "Scripte konnten nicht gespeichert werden."; complete(false) }
                        }
                    },
                    onDeleteRows = { keys ->
                        try {
                            val existing = keys.mapNotNull { key -> scripts.firstOrNull { scriptRowKey(it) == key } }
                            if (existing.isNotEmpty()) {
                                VertriebsindexApi.deleteScripts(existing, bereich)
                                reload()
                            }
                            error = ""
                        } catch (e: CancellationException) { throw e }
                        catch (e: Exception) { error = e.message ?: "Scripte konnten nicht gelöscht werden."; throw e }
                    }
                )
            }
        }
    }
}
